import { blockOfClipboard, clipboardOfBlock } from '@/lib/block-clipboard';
import { isEditableTarget } from '@/lib/editable-target';
import type { ExperienceBlockDraft } from '@matter_extractor/shared';
import type { RefObject } from 'react';
import { useEffect } from 'react';

/**
 * Les touches et le presse-papier de l'éditeur de page (refonte modulaire §9.3 ;
 * grill du 2026-09-23 ; issue #136).
 *
 * `Suppr`, `Retour arrière`, `copy`, `cut`, `paste` : ce composant ÉCOUTE, il ne
 * fait rien. Ce qu'il y a à faire - retirer un bloc et déplacer la sélection,
 * refrapper un collé, refuser au plafond - vit dans `page-editor-body.tsx`,
 * seul endroit qui tienne le brouillon entier.
 *
 * ## Pourquoi un composant qui ne rend rien
 *
 * Les gestes ci-dessous lisent la sélection RÉSOLUE contre la page, qui est
 * calculée après le retour anticipé du corps (« cette page n'est plus dans la
 * timeline »). Un `useEffect` posé là serait un hook après un retour, ce que
 * React interdit ; monté ici, il est au sommet de son propre composant et lit
 * la sélection par ses props. C'est le seul outil que React donne pour « un
 * effet qui dépend d'un état calculé après un retour anticipé ».
 *
 * ## La fenêtre, et pas un `div`
 *
 * Un événement de presse-papier est distribué à `document.activeElement`, qui
 * vaut `<body>` quand personne n'a le focus - c'est-à-dire dans le cas le plus
 * courant, juste après un clic. Un gestionnaire React posé sur le corps de
 * l'éditeur ne le verrait jamais. D'où la fenêtre, et d'où `answersHere`.
 *
 * ## Les événements, et jamais les frappes `Ctrl+C`
 *
 * `clipboardData` ne demande AUCUNE permission, porte le vrai presse-papier du
 * système - donc le bloc traverse un rechargement et un autre onglet - et
 * répond quelle que soit la disposition du clavier, là où « la touche C » n'est
 * pas au même endroit partout. `Ctrl+X` arrive du même coup, sans une ligne de
 * plus : le navigateur en fait un `cut`.
 *
 * ## Pas de tableau de dépendances, délibérément
 *
 * Le gestionnaire lit la sélection du rendu où il a été construit, et un
 * abonnement posé une fois garderait la PREMIÈRE - supprimant éternellement le
 * bloc qui était sélectionné au montage. C'est le régime que
 * `versioned-editor.tsx` argumente pour `Ctrl+Z`, et pour la même raison :
 * réabonner un écouteur de fenêtre par rendu est deux appels que le navigateur
 * résout en temps constant.
 */

export interface StackGesturesProps {
  /** Le corps de l'éditeur : ce qui décide si un geste nous est adressé. */
  readonly root: RefObject<HTMLElement | null>;
  readonly readOnly: boolean;
  /** Le bloc sélectionné, RÉSOLU contre la page - null si la sélection vise autre chose. */
  readonly block: ExperienceBlockDraft | null;
  /** Le temps sélectionné, même règle. */
  readonly tempsId: string | null;
  readonly onRemoveBlock: (blockId: string) => void;
  readonly onRemoveTemps: (tempsId: string) => void;
  readonly onPasteBlock: (block: ExperienceBlockDraft) => void;
}

export function StackGestures({
  root,
  readOnly,
  block,
  tempsId,
  onRemoveBlock,
  onRemoveTemps,
  onPasteBlock,
}: StackGesturesProps) {
  useEffect(() => {
    /**
     * Le geste nous est-il adressé ?
     *
     * Le focus est DANS l'éditeur, ou nulle part. L'aperçu plein écran de #141
     * est un `<dialog>` frère qui prend le focus au montage, et la boîte
     * d'enregistrement aussi : sans cette garde, un `Suppr` frappé devant
     * l'aperçu supprimerait un bloc que personne ne regarde.
     *
     * Un menu ouvert est exclu par le même chemin. Ses touches sont les siennes
     * - les flèches y déplacent le focus d'une tuile à l'autre - et un `Suppr`
     * frappé dedans effacerait, derrière le panneau, le bloc qu'on s'apprêtait
     * à doubler.
     */
    function answersHere(target: EventTarget | null): boolean {
      const node = root.current;
      if (node === null) return false;
      if (target === document || target === document.body) return true;
      if (!(target instanceof Node) || !node.contains(target)) return false;
      return !(target instanceof Element && target.closest('[role="menu"]') !== null);
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Delete' && event.key !== 'Backspace') return;
      if (readOnly || isEditableTarget(event.target) || !answersHere(event.target)) return;
      /*
        Après les gardes seulement : un `preventDefault` posé avant aurait
        avalé l'effacement d'un caractère dans chaque champ de l'écran.
      */
      if (block !== null) {
        event.preventDefault();
        onRemoveBlock(block.id);
        return;
      }
      if (tempsId !== null) {
        event.preventDefault();
        onRemoveTemps(tempsId);
      }
    }

    /**
     * Écrire le bloc sélectionné dans le presse-papier, et le retirer si c'est
     * une coupe.
     *
     * Une SÉLECTION DE TEXTE en cours rend la main au navigateur : on vient de
     * surligner le libellé d'une ligne à la souris, et détourner ce `copy`-là
     * mettrait un objet JSON à la place des mots qu'on visait. La question se
     * pose ici et pas dans `isEditableTarget` : hors d'un champ, on peut très
     * bien avoir surligné du texte ordinaire.
     */
    function onCopy(event: ClipboardEvent, cut: boolean) {
      if (isEditableTarget(event.target) || !answersHere(event.target)) return;
      if (!(window.getSelection()?.isCollapsed ?? true)) return;
      if (block === null || (cut && readOnly)) return;
      event.clipboardData?.setData('text/plain', clipboardOfBlock(block));
      event.preventDefault();
      if (cut) onRemoveBlock(block.id);
    }

    function onCut(event: ClipboardEvent) {
      onCopy(event, true);
    }

    function onPlainCopy(event: ClipboardEvent) {
      onCopy(event, false);
    }

    /**
     * Coller : le texte du presse-papier est du contenu NON FIABLE.
     *
     * `blockOfClipboard` le passe par Zod avant tout usage, et un presse-papier
     * qui ne porte pas un bloc de cette application ne fait rien - pas même un
     * refus. Coller une phrase dans l'éditeur n'est pas une erreur, c'est juste
     * autre chose, et le navigateur en fait ce qu'il en fait.
     */
    function onPaste(event: ClipboardEvent) {
      if (readOnly || isEditableTarget(event.target) || !answersHere(event.target)) return;
      const pasted = blockOfClipboard(event.clipboardData?.getData('text/plain') ?? '');
      if (pasted === null) return;
      event.preventDefault();
      onPasteBlock(pasted);
    }

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('copy', onPlainCopy);
    window.addEventListener('cut', onCut);
    window.addEventListener('paste', onPaste);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('copy', onPlainCopy);
      window.removeEventListener('cut', onCut);
      window.removeEventListener('paste', onPaste);
    };
  });

  return null;
}
